/**
 * Tenant-wide central routing settings (`GET/PUT /api/v1/settings/central-routing`).
 */

import { workspaceHumanAgentSettingsClient } from "@/api/workspaceHumanAgentSettingsClient";
import { mapRoutingFromApi, mapRoutingToApi } from "@/api/workspaceRoutingSettingsMapper";
import { fetchRoutingAgentCandidates } from "@/api/workspaceRoutingAgentsApi";

/** @typedef {import('@/api/agentRoutingApi').AgentRoutingSettings} AgentRoutingSettings */

export const DEFAULT_CENTRAL_ROUTING = {
  roundRobinEnabled: false,
  agentOrderIds: [],
  roundRobinCursor: 0,
  agents: [],
};

export async function getCentralRoutingSettings() {
  try {
    const [routing, dir] = await Promise.all([
      workspaceHumanAgentSettingsClient.getCentralRouting(),
      fetchRoutingAgentCandidates().catch(() => ({ agents: [] })),
    ]);
    const mapped = mapRoutingFromApi(
      /** @type {Record<string, unknown>} */ (routing),
      /** @type {Array<{ id: number, name: string, email?: string }>} */ (dir.agents || [])
    );
    return { data: mapped };
  } catch {
    return { data: { ...DEFAULT_CENTRAL_ROUTING, agents: [], agentOrderIds: [] } };
  }
}

export async function saveCentralRoutingSettings(patch) {
  const prev = (await getCentralRoutingSettings()).data;
  const merged = {
    roundRobinEnabled: typeof patch.roundRobinEnabled === "boolean" ? patch.roundRobinEnabled : prev.roundRobinEnabled,
    agents: patch.agents ?? prev.agents,
    agentOrderIds: patch.agentOrderIds ?? prev.agentOrderIds,
    roundRobinCursor: typeof patch.roundRobinCursor === "number" ? patch.roundRobinCursor : prev.roundRobinCursor,
  };
  const body = mapRoutingToApi(merged);
  const saved = await workspaceHumanAgentSettingsClient.putCentralRouting(body);
  const dir = await fetchRoutingAgentCandidates().catch(() => ({ agents: [] }));
  const data = mapRoutingFromApi(
    /** @type {Record<string, unknown>} */ (saved),
    /** @type {Array<{ id: number, name: string, email?: string }>} */ (dir.agents || [])
  );
  return { data };
}
